import React from 'react';
import PropTypes from 'prop-types';
import sumBy from 'lodash.sumby';
import moment from 'moment';
import currencyFormatter from 'currency-formatter';
import {ResponsiveContainer, ComposedChart, XAxis, YAxis, Bar, Tooltip} from 'recharts';
import {sizes, svgs} from 'epic-diesel-common/src/constants';
import {Icon, Message} from 'epic-diesel-common';
import Copy from '../../common/Copy';
import CustomTooltip from './CustomTooltip';
import Tip from '../../common/Tip';
import styles from './Card.pcss';
import {getMessage} from '../../common/Message';
import classNames from 'classnames';
//import {Icon} from 'epic-diesel-common';

const TYPE = {
    EARNINGS: 'earnings',
    CLICKS: 'clicks',
    CONVERSIONS: 'conversions'
};

const sumClick = num => {
    if (num < 100000) {
        return num;
    }
    return `${(num / 1000).toFixed(0)}K`;
};

export default class Card extends React.Component {
    static propTypes = {
        messages: PropTypes.object,
        name: PropTypes.string,
        data: PropTypes.object,
        time: PropTypes.string,
        fill: PropTypes.string,
        startExpanded: PropTypes.bool,
        enableLink: PropTypes.bool,
        showLink: PropTypes.bool
    };

    constructor(props) {
        super(props);
        const {data, startExpanded} = props;
        const hasData = data && data.data && data.data.length;

        this.state = {
            collapsed: !startExpanded || !hasData,
            type: TYPE.EARNINGS,
            hasData
        };
    }

    UNSAFE_componentWillReceiveProps(newProps) {
        const {data, startExpanded} = newProps;
        const hasData = data && data.data && data.data.length;

        const newState = {
            collapsed: !startExpanded || !hasData,
            type: TYPE.EARNINGS,
            hasData
        };

        this.setState(newState);
    }

    toggleCollapsed = () => {
        if (this.state.hasData) {
            this.setState({collapsed: !this.state.collapsed});
        }
    };

    setType = type => () => {
        const newState = {
            type,
            collapsed: false
        };
        this.setState(newState);
    };

    formatDate = data => {
        for (let i = 0; i < data.length; i++) {
            const {date} = data[i];
            const splitDate = date.split('-');

            if (date) {
                switch (this.props.time) {
                    case 'year':
                        data[i].dateFormatted = getMessage(
                            this.props.messages,
                            `epic.affiliate.earnings.card.months.${parseInt(splitDate[1], 10)}`
                        );
                        break;

                    case 'six months':
                        data[i].dateFormatted = `Week ${moment(date).week()}`;
                        break;

                    case 'month':
                    default:
                        data[i].dateFormatted = `${splitDate[1]}-${splitDate[2]}`;
                        break;
                }
            }
        }

        return data;
    };

    render() {
        const {collapsed, type, hasData} = this.state;
        const {name, data, fill, messages, enableLink, showLink} = this.props;
        const formattedData = this.formatDate(data.data);

        const copyReferralLink = messages
            ? getMessage(messages, 'epic.affiliate.earnings.card.copy_link')
            : '';
        const copyReferralLinkSuccess = messages
            ? getMessage(messages, 'epic.affiliate.earnings.card.copy_link.success')
            : '';
        const copyReferralLinkFailed = messages
            ? getMessage(messages, 'epic.affiliate.earnings.card.copy_link.failed')
            : '';
        const disabledTip = messages
            ? getMessage(messages, 'epic.affiliate.earnings.card.copy_link.disabled')
            : '';

        const isFortnite = name && name.toUpperCase() === 'FORTNITE';
        const isBattleBreakers = name && name.toUpperCase() === 'BATTLE BREAKERS';

        const {icon, shareRate} = data;

        let shareRateText = null;
        if (isFortnite) {
            shareRateText = (
                <div className={styles.withholdings}>
                    <Message code="epic.affiliate.earnings.my_earnings.fortnite_share_rate" />
                    <br />
                    <Message code="epic.affiliate.earnings.my_earnings.withholdings" />
                </div>
            );
        } else if (isBattleBreakers) {
            shareRateText = <div className={styles.withholdings} />;
        } else {
            const revenueShareString = messages
                ? getMessage(messages, 'epic.affiliate.earnings.card.revenue_share')
                : '';

            shareRateText = (
                <div className={styles.withholdings}>
                    {revenueShareString} {shareRate}%
                </div>
            );
        }

        const supportersRawCount = sumBy(formattedData, 'referrals');
        const supporters = hasData ? sumClick(supportersRawCount) : null;

        //const conversionsRawCount = sumBy(formattedData, 'conversions');
        //const conversions = hasData ? sumClick(conversionsRawCount) : null;

        const earnings = hasData
            ? currencyFormatter.format(sumBy(formattedData, 'earnings'), {
                  code: data[0] ? data[0].currency : 'USD'
              })
            : null;

        //const supportersText = isFortnite ? <Message code="epic.affiliate.earnings.card.supporters" /> : <Message code="epic.affiliate.earnings.card.clicks" />;

        const supportersTipContent = (
            <div className={styles.tipContent}>
                <p>
                    <Message code="epic.affiliate.earnings.my_earnings.tooltip.supporters" />
                </p>
            </div>
        );

        const earningsTipContent = (
            <div className={styles.tipContent}>
                <p>
                    <Message code="epic.affiliate.earnings.my_earnings.tooltip.earnings" />
                </p>
            </div>
        );

        return (
            <div className={styles.card}>
                <div className={styles.metrics}>
                    <div className={styles.site} onClick={this.toggleCollapsed}>
                        {!icon && <Icon icon={svgs.EPIC} className={styles.gameIcon} />}
                        {isFortnite && <Icon icon={svgs.FORTNITE} className={styles.gameIcon} />}
                        {icon && !isFortnite && (
                            <img className={styles.gameIcon} src={icon} alt={name} />
                        )}
                        <div className={styles.textWrapper}>
                            <h4>{name}</h4>
                            {shareRateText}
                        </div>
                    </div>
                    {!hasData && isFortnite && (
                        <div className={styles.helperText}>
                            <Message code="epic.affiliate.earnings.my_earnings.how_to" />
                        </div>
                    )}
                    {!hasData && isBattleBreakers && (
                        <div className={styles.helperText}>
                            <Message code="epic.affiliate.earnings.my_earnings.how_to_battlebreakers" />
                        </div>
                    )}
                    {!hasData && !(isFortnite || isBattleBreakers) && (
                        <div className={styles.helperText}>
                            <Message code="epic.affiliate.earnings.my_earnings.how_to_diesel" />
                        </div>
                    )}
                    {supporters !== null && (
                        <Tip
                            className={styles.tip}
                            content={supportersTipContent}
                            position="top"
                            onClick={this.setType(TYPE.CLICKS)}>
                            <div
                                className={`${
                                    !collapsed && type === TYPE.CLICKS ? styles.active : styles.tab
                                }`}
                                style={{
                                    borderTopColor: !collapsed && type === TYPE.CLICKS ? fill : ''
                                }}>
                                <span title={supportersRawCount}>{supporters}</span>
                                <Message code="epic.affiliate.earnings.card.supporters" />
                            </div>
                        </Tip>
                    )}
                    {/* {!isFortnite && conversions !== null &&
                        <div
                            className={`${!collapsed && type === TYPE.CONVERSIONS ? styles.active : styles.tab}`}
                            style={{borderTopColor: !collapsed && type === TYPE.CONVERSIONS ? fill : ''}}
                            onClick={this.setType(TYPE.CONVERSIONS)}>
                            <span>{sumClick(sumBy(formattedData, 'conversions'))}</span>
                            <Message code="epic.affiliate.earnings.card.conversions" />
                        </div>
                    } */}

                    {earnings !== null && (
                        <Tip
                            className={styles.tip}
                            content={earningsTipContent}
                            position="top"
                            onClick={this.setType(TYPE.EARNINGS)}>
                            <div
                                className={`${
                                    !collapsed && type === TYPE.EARNINGS
                                        ? styles.active
                                        : styles.tab
                                } ${styles.earningsTab}`}
                                style={{
                                    borderTopColor: !collapsed && type === TYPE.EARNINGS ? fill : ''
                                }}>
                                <span>{earnings}</span>
                                <Message code="epic.affiliate.earnings.card.estimated_earnings" />
                            </div>
                        </Tip>
                    )}

                    <div className={styles.icons}>
                        {/* <div>
                            <Icon
                                className={styles.icon}
                                icon={svgs.DOWNLOAD}
                                size={sizes.XSMALL} />
                        </div> */}
                        {showLink && data.link && (
                            <Copy
                                text={data.link}
                                tip={copyReferralLink}
                                successTip={copyReferralLinkSuccess}
                                errorTip={copyReferralLinkFailed}
                                disabledTip={disabledTip}
                                enableLink={enableLink}>
                                <Icon
                                    className={classNames(styles.icon, styles.link)}
                                    icon={svgs.LINK}
                                    size={sizes.XSMALL}
                                />
                            </Copy>
                        )}
                    </div>
                </div>
                <div className={`${styles.chart} ${!collapsed ? styles.showChart : ''}`}>
                    <ResponsiveContainer className={styles.rechartsWrapper}>
                        <ComposedChart
                            data={formattedData}
                            barCategoryGap={1}
                            margin={{
                                top: 5,
                                right: 0,
                                left: 20,
                                bottom: 5
                            }}>
                            <XAxis dataKey="dateFormatted" stroke="#606060" />
                            <YAxis yAxisId="right" orientation="right" stroke="#606060" />
                            <Tooltip
                                content={<CustomTooltip isCurrency={type === TYPE.EARNINGS} />}
                            />
                            {type === TYPE.EARNINGS && (
                                <Bar dataKey="earnings" yAxisId="right" fill={fill} />
                            )}
                            {type === TYPE.CLICKS && (
                                <Bar dataKey="referrals" yAxisId="right" fill={fill} />
                            )}
                            {type === TYPE.CONVERSIONS && (
                                <Bar dataKey="conversions" yAxisId="right" fill={fill} />
                            )}
                        </ComposedChart>
                    </ResponsiveContainer>
                </div>
            </div>
        );
    }
}
